/* ScoreBadge — a score cannot be rendered without its coverage, and cannot be faked when absent.
 *
 * MODULE_PROMPTS.md, lumiq-web: "coverage is displayed beside every score, never omitted", and
 * scoring: "a 98 on 4% coverage is a lie, and shipping the score without coverage is the product
 * telling that lie."
 *
 * So `coverage` is a REQUIRED prop, not an optional one with a sensible default. `<ScoreBadge
 * score={98} />` is a type error, which is the only version of this rule that survives a deadline: a
 * lint rule can be disabled and a code review can be skipped, but the build cannot be argued with.
 *
 * **Both props are nullable, and that was a correction.** The first version typed them
 * `score: number` / `coverage: number`, which is unrenderable for the two cases the contract works
 * hardest to carry: a weighted dimension with no rules has no score, and a dataset version with no
 * columns has no coverage ratio. A caller holding `number | null` would have had to write `?? 0` to
 * satisfy those types — turning every honest absence into a zero at the last hop, in the layer where
 * it looks like a formatting detail.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export interface ScoreBadgeProps {
  /** `null` means no evidence. It never means zero — see the module comment. */
  score: number | null;
  /** Percentage of columns with at least one active rule, or `null` when there are no columns. */
  coverage: number | null;
}

/* Coverage bands, not score bands. A high score on thin coverage is the misleading case, so the
 * visual weight follows how much of the dataset the score actually describes.
 *
 * `--status-ok`, not `--accent`. These three bands are a red-amber-green scale, and the top of it
 * means "this score describes enough of the dataset to trust" — a health verdict, not a brand
 * highlight. They read the same while the accent happened to be lime; the day the accent became blue,
 * `--accent` here would have put the healthy end of a traffic light in the brand colour. */
function coverageTone(coverage: number | null): string {
  if (coverage === null) return 'var(--text-muted)';
  if (coverage >= 80) return 'var(--status-ok)';
  if (coverage >= 40) return 'var(--status-warn)';
  return 'var(--status-error)';
}

export function ScoreBadge({ score, coverage }: ScoreBadgeProps): ReactElement {
  const { t } = useTranslation();

  /* Rendered as words, not as a dash. An em dash in a numeric column reads as a failed load, and a
   * reader who thinks the number failed to arrive will refresh rather than investigate why nothing
   * was measured. */
  const measured = score !== null;

  return (
    <span
      className="lumiq-numeric lq-scorebadge"
      aria-label={
        measured
          ? t('score.ariaLabel', { score, coverage: coverage ?? 0 })
          : t('score.ariaLabelNoEvidence')
      }
    >
      {measured ? (
        <strong className="lq-scorebadge__value">{score.toFixed(1)}</strong>
      ) : (
        <span className="lq-scorebadge__absent">{t('score.noEvidence')}</span>
      )}
      {/* Same element, deliberately: coverage is not a tooltip or a secondary detail that a future
       * layout change can drop. It is part of the number's meaning. */}
      <span
        className="lq-scorebadge__coverage"
        // The one genuinely computed value: the tone depends on the coverage figure itself.
        style={{ color: coverageTone(coverage) }}
      >
        {coverage === null
          ? t('score.noColumns')
          : `${String(coverage)}% ${t('score.coverageLabel')}`}
      </span>
    </span>
  );
}
